import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TableModule} from 'primeng/table';
import {Button} from 'primeng/button';
import {InputText} from 'primeng/inputtext';
import {FormsModule} from '@angular/forms';
import {ConfirmationService} from 'primeng/api';
import {RestApiService} from '../../../services/api/rest-api.service';
import {RoutingConstants} from '../../../models/routing/routing-constants';
import {Routing} from '../../../models/routing/routing';
import {
  MovieImportCommitResponse,
  MovieImportMode,
  MovieImportMigrationResponse,
  MovieImportRow
} from '../../../models/vision/movie-import/movie-import-migration-response';
import {ToastService} from '../../../services/toast.service';
import {MovieGenre} from '../../../models/vision/movie';

@Component({
  selector: 'app-recap-catalogue-import',
  standalone: true,
  imports: [
    TableModule,
    Button,
    InputText,
    FormsModule
  ],
  templateUrl: './recap-catalogue-import.component.html',
  styleUrl: './recap-catalogue-import.component.scss'
})
export class RecapCatalogueImportComponent implements OnInit {
  loading = false;
  migrationId = '';
  migration?: MovieImportMigrationResponse;
  errorMessage = '';
  searchText = '';
  selectedEntries: MovieImportRow[] = [];
  missingTaxonomyTranslations: MissingTaxonomyTranslation[] = [];
  savingTaxonomies = false;
  taxonomiesConfirmed = false;
  committing = false;
  commitResponse?: MovieImportCommitResponse;

  constructor(private readonly api: RestApiService,
              private readonly route: ActivatedRoute,
              private readonly router: Router,
              private readonly toastService: ToastService,
              private readonly confirmationService: ConfirmationService) {
  }

  async ngOnInit() {
    this.migrationId = this.route.snapshot.params[RoutingConstants.params.id.substring(1)] ?? '';
    await this.load();
  }

  obj(item: MovieImportRow) {
    return item;
  }


  get entries() {
    return this.migration?.entries ?? [];
  }

  get filteredEntries() {
    const filter = this.normalize(this.searchText);
    if (!filter) {
      return this.entries;
    }

    return this.entries.filter(row => [
      row.movie.title,
      row.movie.year,
      row.movie.direction,
      row.genreName,
      row.subGenreName
    ].some(value => this.normalize(value).includes(filter)));
  }

  get selectedCount() {
    return this.selectedEntries.length;
  }

  get missingGenres() {
    return this.migration?.missingTaxonomies?.genres ?? [];
  }

  get missingSubGenres() {
    return this.migration?.missingTaxonomies?.subGenres ?? [];
  }

  get hasMissingTaxonomies() {
    return this.missingGenres.length > 0 || this.missingSubGenres.length > 0;
  }

  get mustResolveTaxonomies() {
    return this.hasMissingTaxonomies && !this.taxonomiesConfirmed;
  }

  get areTaxonomyTranslationsValid() {
    return this.missingTaxonomyTranslations.every(item => this.normalize(item.englishName).length > 0);
  }

  get newMoviesCount() {
    return this.migration?.entries.filter(entry => !entry.movie.key).length ?? 0;
  }


  async load() {
    if (!this.migrationId) {
      this.errorMessage = 'MigrationId mancante.';
      return;
    }

    try {
      this.loading = true;
      this.errorMessage = '';
      const response = await this.api.movies.importMigration(this.migrationId);
      this.migration = response.data;
      this.selectedEntries = [...this.entries];
      this.prepareMissingTaxonomyTranslations();
    } catch {
      this.errorMessage = 'Impossibile caricare il recap import.';
      this.toastService.error('Errore', this.errorMessage);
    } finally {
      this.loading = false;
    }
  }

  imageCount(row: MovieImportRow) {
    return [
      row.images?.coverImageIt,
      row.images?.coverImageEn,
      row.images?.sliderImageIt,
      row.images?.sliderImageEn,
    ].filter(value => !!value).length;
  }

  goToImport() {
    this.router.navigateByUrl(Routing.movies.catalogueImport(true));
  }

  goToCatalogue() {
    this.router.navigateByUrl(`/${Routing.dashboard.getBasePath()}`);
  }

  async saveMissingTaxonomies() {
    if (!this.areTaxonomyTranslationsValid || this.savingTaxonomies) {
      return;
    }

    try {
      this.savingTaxonomies = true;
      await Promise.all(this.missingTaxonomyTranslations.map(item => {
        const genre = new MovieGenre();
        genre.name = item.name.trim();
        genre.englishName = item.englishName.trim();
        genre.isSubGenre = item.isSubGenre;
        return this.api.genres.saveGenre(genre);
      }));

      this.taxonomiesConfirmed = true;
      this.toastService.success('Tassonomie salvate', 'Generi e sottogeneri salvati con successo');
      await this.load();
    } catch {
      this.toastService.error('Errore', 'Impossibile salvare generi e sottogeneri mancanti');
    } finally {
      this.savingTaxonomies = false;
    }
  }

  selectFiltered() {
    const selected = new Set(this.selectedEntries);
    this.filteredEntries.forEach(entry => selected.add(entry));
    this.selectedEntries = this.entries.filter(entry => selected.has(entry));
  }

  deselectFiltered() {
    const filtered = new Set(this.filteredEntries);
    this.selectedEntries = this.selectedEntries.filter(entry => !filtered.has(entry));
  }

  confirmImport() {
    if (!this.selectedCount) {
      return;
    }

    this.confirmationService.confirm({
      message: `sei sicuro di voler importare/aggiornare ${this.selectedCount} film?`,
      header: 'Conferma importazione',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      acceptLabel: 'Conferma importazione',
      rejectLabel: 'Annulla',
      rejectButtonStyleClass: 'p-button-secondary p-button-text',
      accept: async () => {
        await this.commitImport();
      }
    });
  }

  async commitImport() {
    if (!this.selectedCount || this.committing) {
      return;
    }

    try {
      this.committing = true;
      const response = await this.api.movies.commitImport(this.migrationId, {
        importMode: MovieImportMode.Upsert,
        entries: this.selectedEntries.map(row => ({
          key: row.movie.key || null,
          rowNumber: row.rowNumber || null
        }))
      });

      this.commitResponse = response.data;
      this.toastService.success('Importazione completata', 'Catalogo importato correttamente.');
    } catch {
      this.toastService.error('Errore', 'Impossibile completare l\'importazione.');
    } finally {
      this.committing = false;
    }
  }

  private normalize(value: unknown) {
    return String(value ?? '').trim().toLowerCase();
  }

  private prepareMissingTaxonomyTranslations() {
    if (!this.hasMissingTaxonomies || this.taxonomiesConfirmed) {
      this.missingTaxonomyTranslations = [];
      return;
    }

    this.missingTaxonomyTranslations = [
      ...this.missingGenres.map(name => ({name, englishName: '', isSubGenre: false})),
      ...this.missingSubGenres.map(name => ({name, englishName: '', isSubGenre: true}))
    ];
  }
}

interface MissingTaxonomyTranslation {
  name: string;
  englishName: string;
  isSubGenre: boolean;
}
